/** One guest screen, laid out as bookingmgmt `components/Layout.js` does: the app bar, then
 *  the sidebar beside the step from `lg` (`wide`), or above it on a phone, with a phone's
 *  progress bar under the app bar and its step buttons in a bar at the foot. Rendered
 *  under the guest theme by `GuestFormPreview`; the page's own state (typed values,
 *  touched fields) is the preview's, passed in. */
import { Box } from '@mui/material';

import type { PreCheckField } from '../../model';
import { GUEST } from '../guestTokens';
import {
  CHECKIN_STEP,
  confirmationLines,
  DONE_STEP,
  introText,
  withDefaults,
  type GuestPreviewOptions,
  type GuestPreviewSample,
  type GuestPreviewSettings,
  type GuestPreviewWaiver,
  type PreviewStepDef,
} from '../previewModel';

import { GuestAppBar, GuestSidebar, type SidebarStep, type StepStatus } from './GuestChrome';
import type { FieldValues } from './GuestFields';
import { CheckinScreen, DoneScreen, ParticipantButtons, ParticipantScreen, participantNames } from './GuestSteps';

export interface GuestPageProps {
  settings: GuestPreviewSettings;
  options: GuestPreviewOptions;
  sample: GuestPreviewSample;
  steps: readonly PreviewStepDef[];
  current: PreviewStepDef;
  onStep: (id: string) => void;
  /** The current participant step's fields (empty on the other steps). */
  fields: readonly PreCheckField[];
  values: readonly FieldValues[];
  touched: Readonly<Record<string, boolean>>;
  onChange: (id: string, value: unknown) => void;
  onBlur: (id: string) => void;
  waivers: readonly GuestPreviewWaiver[];
  lang: string | null;
  onLang: (lang: string) => void;
  wide: boolean;
}

export function GuestPage(p: GuestPageProps) {
  const { steps, current, wide } = p;
  const participantSteps = steps.filter((s) => s.participant !== null);
  const done = current.id === DONE_STEP;
  // The sidebar counts the participant steps and Pre-check-in; Done is not a step of it.
  const index = done ? participantSteps.length + 1 : current.participant ?? participantSteps.length;
  const total = participantSteps.length + 1;
  const statusOf = (i: number): StepStatus => (done || i < index ? 'completed' : i === index ? 'current' : 'pending');
  const sidebarSteps: SidebarStep[] = [
    ...participantSteps.map((def, i) => ({
      def,
      label: i === 0 ? 'Lead Booker' : `Guest ${i}`,
      status: statusOf(i),
      active: def.id === current.id,
      lead: i === 0,
    })),
    {
      def: steps.find((s) => s.id === CHECKIN_STEP)!,
      label: 'Pre-check-in',
      status: done ? 'completed' : index === participantSteps.length ? 'current' : 'pending',
      active: current.id === CHECKIN_STEP,
      lead: false,
    },
  ];
  const reachable = !done || withDefaults(p.settings).allow_edit_after_checkin === true;
  const next = () => {
    const at = steps.findIndex((s) => s.id === current.id);
    p.onStep(steps[Math.min(at + 1, steps.length - 1)]!.id);
  };
  const lastParticipant = participantSteps[participantSteps.length - 1]!.id;

  const screen =
    current.participant !== null ? (
      <ParticipantScreen
        participant={current.participant}
        fields={p.fields}
        values={p.values[current.participant] ?? {}}
        onChange={p.onChange}
        touched={p.touched}
        onBlur={p.onBlur}
        options={p.options}
        wide={wide}
        onNext={next}
      />
    ) : current.id === CHECKIN_STEP ? (
      <CheckinScreen
        names={participantNames(p.values, participantSteps.length)}
        waivers={p.waivers}
        lang={p.lang}
        onLang={p.onLang}
        variables={p.sample.variables}
        wide={wide}
        onDone={() => p.onStep(DONE_STEP)}
        onCancel={() => p.onStep(lastParticipant)}
      />
    ) : (
      <DoneScreen lines={confirmationLines(p.settings, p.sample)} wide={wide} />
    );

  return (
    <Box data-guest-page={wide ? 'desktop' : 'phone'} data-guest-step={current.id} sx={{ bgcolor: GUEST.pageBg, display: 'flex', flexDirection: 'column' }}>
      <GuestAppBar sample={p.sample} wide={wide} progress={!wide && !done ? Math.min(100, (index / total) * 100) : null} />
      <Box
        sx={{
          maxWidth: wide ? GUEST.maxWidthPx : undefined,
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: wide ? 'row' : 'column',
          pt: wide ? 4 : 0.5,
          pb: 4,
          px: wide ? 0 : 1,
          gap: wide ? 3 : 2,
          alignItems: wide ? 'flex-start' : 'stretch',
        }}
      >
        <Box component="nav" aria-label="pre-check-in steps" sx={{ width: wide ? GUEST.sidebarPx : '100%', flexShrink: 0, mb: wide ? 0 : 2 }}>
          <GuestSidebar
            sample={p.sample}
            intro={introText(p.settings)}
            steps={sidebarSteps}
            position={done ? null : { n: index + 1, of: total }}
            reachable={reachable}
            onStep={(s) => p.onStep(s.id)}
            wide={wide}
          />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: wide ? 2 : 1,
            boxShadow: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            alignSelf: 'flex-start',
            width: wide ? `calc(100% - ${GUEST.sidebarPx}px - 24px)` : '100%',
          }}
        >
          {screen}
        </Box>
      </Box>
      {!wide && current.participant !== null && <ParticipantButtons lead={current.participant === 0} wide={false} onNext={next} />}
    </Box>
  );
}
